import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { Code, CheckCircle2, XCircle } from 'lucide-react';
import { AGENT_TOOLS } from '@/config/agent-tools';
import { supabase } from '@/integrations/supabase/client';

interface ToolConfig {
  name: string;
  enabled: boolean;
  description: string;
  parameters: any;
}

export default function ToolConfigurator() {
  const { toast } = useToast();
  const { orgId } = useAuth();
  const [tools, setTools] = useState<ToolConfig[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (orgId) loadToolConfig();
  }, [orgId]);

  const loadToolConfig = async () => {
    if (!orgId) return;

    // Load tool configuration from preferences
    const { data } = await supabase.functions.invoke('agent-memory-service', {
      body: {
        action: 'get_preferences',
        data: { org_id: orgId }
      }
    });

    const toolConfig = data?.preferences?.enabled_tools?.value || {};
    
    const toolList = Object.entries(AGENT_TOOLS).map(([key, tool]) => ({
      name: key,
      enabled: toolConfig[key] !== false, // Default to enabled
      description: tool.description,
      parameters: tool.parameters
    }));

    setTools(toolList);
  };

  const handleToggle = async (toolName: string, enabled: boolean) => {
    setTools(prev => prev.map(t => 
      t.name === toolName ? { ...t, enabled } : t
    ));
  };

  const handleSave = async () => {
    if (!orgId) return;
    setSaving(true);

    try {
      const enabledTools = tools.reduce((acc, tool) => ({
        ...acc,
        [tool.name]: tool.enabled
      }), {});

      const { error } = await supabase.functions.invoke('agent-memory-service', {
        body: {
          action: 'set_preference',
          data: {
            org_id: orgId,
            key: 'enabled_tools',
            value: enabledTools,
            confidence: 1.0
          }
        }
      });

      if (error) throw error;

      toast({
        title: 'Tool Configuration Saved',
        description: 'Agent will use the updated tool set',
      });
    } catch (error) {
      console.error('Error saving tools:', error);
      toast({
        title: 'Error',
        description: 'Failed to save tool configuration',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const enabledCount = tools.filter(t => t.enabled).length;

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Tool Configuration</CardTitle>
          <CardDescription>
            Enable or disable specific tools available to Bridgette. Disabled tools will not be accessible during conversations.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Badge variant="secondary" className="flex items-center gap-2">
                <CheckCircle2 className="w-3 h-3" />
                {enabledCount} Enabled
              </Badge>
              <Badge variant="outline" className="flex items-center gap-2">
                <XCircle className="w-3 h-3" />
                {tools.length - enabledCount} Disabled
              </Badge>
            </div>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? 'Saving...' : 'Save Configuration'}
            </Button>
          </div>

          <div className="space-y-4">
            {tools.map((tool) => (
              <Card key={tool.name} className="border-2">
                <CardContent className="pt-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <Code className="w-5 h-5 text-primary" />
                        <div>
                          <h3 className="font-semibold text-lg">{tool.name}</h3>
                          <p className="text-sm text-muted-foreground">{tool.description}</p>
                        </div>
                      </div>
                      
                      <div className="ml-8 p-3 bg-muted/50 rounded-lg">
                        <p className="text-xs font-mono text-muted-foreground">
                          Parameters: {JSON.stringify(tool.parameters, null, 2)}
                        </p>
                      </div>
                    </div>
                    
                    <Switch
                      checked={tool.enabled}
                      onCheckedChange={(checked) => handleToggle(tool.name, checked)}
                    />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
